import PropTypes from "prop-types";
import { useState } from "react";
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CheckIcon } from "../viewer/viewer-icons";

const BG = "rgba(120,120,120,0.9)";
const START_DELAYS = [0, 3, 5, 10]; // seconds; 0 = no timer

// scan-timer control
// autoStop: image count to stop the scan at, 0 = off (stop manually);
// autoStopCount is the minimum scan size, offered along with two larger runs.
export const ScanTimerButton = ({
  startDelay,
  onStartDelay,
  autoStop,
  onAutoStop,
  autoStopCount,
}) => {
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);

  const autoStops = [0, autoStopCount, autoStopCount + 20, autoStopCount + 35];

  const check = (on) =>
    on ? <CheckIcon size={18} color={theme.palette.primary.main} /> : null;

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Scan timers"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          position: "relative",
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "none",
          p: 0,
          cursor: "pointer",
          backgroundColor: BG,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {startDelay > 0 ? (
          // the selected start delay in place of the icon
          <Box
            component="span"
            sx={{ color: "#fff", fontSize: 15, fontWeight: "bold" }}
          >
            {startDelay}s
          </Box>
        ) : (
          // stopwatch icon (no start timer)
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
            <path d="M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42C16.07 4.74 14.12 4 12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
          </svg>
        )}
        {/* corner badge with the auto stop image count when auto stop is on */}
        {autoStop > 0 && (
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: -4,
              right: -6,
              minWidth: 20,
              height: 16,
              px: "4px",
              boxSizing: "border-box",
              borderRadius: "8px",
              backgroundColor: "primary.main",
              border: "1.5px solid #fff",
              color: "#fff",
              fontSize: 10,
              fontWeight: "bold",
              lineHeight: "13px",
              textAlign: "center",
            }}
          >
            {autoStop}
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 220 } } }}
        sx={{ zIndex: 1500 }}
      >
        <ListSubheader sx={{ lineHeight: "32px" }}>Start timer</ListSubheader>
        {START_DELAYS.map((delay) => (
          <MenuItem
            key={delay}
            dense
            selected={startDelay === delay}
            onClick={() => {
              onStartDelay(delay);
              setAnchor(null);
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {check(startDelay === delay)}
            </ListItemIcon>
            <ListItemText>
              {delay === 0 ? "Off" : `${delay} seconds`}
            </ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <ListSubheader sx={{ lineHeight: "32px" }}>Auto stop</ListSubheader>
        {autoStops.map((count) => (
          <MenuItem
            key={count}
            dense
            selected={autoStop === count}
            onClick={() => {
              onAutoStop(count);
              setAnchor(null);
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {check(autoStop === count)}
            </ListItemIcon>
            <ListItemText>
              {count === 0 ? "Off" : `Stop at ${count} images`}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

ScanTimerButton.propTypes = {
  startDelay: PropTypes.number.isRequired,
  onStartDelay: PropTypes.func.isRequired,
  autoStop: PropTypes.number.isRequired,
  onAutoStop: PropTypes.func.isRequired,
  autoStopCount: PropTypes.number.isRequired,
};
